<template>
	<div>
		<MappingSetupInfo utilClasses="mx-5 my-1" />
		<ChooseDynamicDataMappingCard
			:node="node"
			:action="nodeAction"
			:serviceId="nodeServiceId"
			:serviceVersionId="nodeServiceVersion"
			:hasEditPermissions="hasEditPermissions"
		/>
		<div v-if="filledMapper">
			<DynamicParamsWriteActionDispatcher
				v-if="isWriteAction"
				:params="allActionParams"
				:node="node"
				:action="nodeAction"
				:service="nodeService"
				:buildFieldKeyFunc="buildFieldKey"
				@update-input-field="updateInputField"
				@update-input-field-label="updateInputFieldLabel"
				@update-input-fallback="updateInputFallback"
				:hasEditPermissions="hasEditPermissions"
			/>
			<DynamicParamsReadActionDispatcher
				v-else
				:params="allActionParams"
				:node="node"
				:action="nodeAction"
				:service="nodeService"
				:buildFieldKeyFunc="buildFieldKey"
				@update-input-field="updateInputField"
				@update-input-field-label="updateInputFieldLabel"
				@update-input-fallback="updateInputFallback"
				:hasEditPermissions="hasEditPermissions"
			/>
		</div>
	</div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

import { mapGetters, mapActions } from 'vuex';
import { getService } from '@/store/modules/apps/getters';
import { getWidgetFieldData } from '@/store/modules/widgets/getters';
import { getAction, getActionParams } from '@/store/modules/actions/getters';
import { editNodeOriginal, editNodeParams, editNode } from '@/store/modules/selectedNode/getters';

import AppService from '@/utils/AppService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { getAccountConnection } from '@/store/modules/credentials/getters';

import ParamsService from '@/utils/ParamsService';
import DynamicDataService from '@/utils/dynamic-data-service';
import DynamicParamsWriteActionDispatcher from '@/components/BlockEditor/MappingSetup/DynamicParamsWriteActionDispatcher';
import DynamicParamsReadActionDispatcher from '@/components/BlockEditor/MappingSetup/DynamicParamsReadActionDispatcher';
import MappingSetupInfo from '@/components/BlockEditor/MappingSetup/MappingSetupInfo';
import ChooseDynamicDataMappingCard from '@/components/BlockEditorWidget/WidgetFields/DynamicDataMapping/ChooseDynamicDataMappingCard';

export default {
	name: 'ActionParamsDispatcherDataProvider',
	props: {
		node: { type: Object },
	},
	components: {
		DynamicParamsWriteActionDispatcher,
		DynamicParamsReadActionDispatcher,
		MappingSetupInfo,
		ChooseDynamicDataMappingCard,
	},
	computed: {
		...mapGetters([
			getAction.fullPath,
			getService.fullPath,
			editedWorkflowVersion.fullPath,
			getActionParams.fullPath,
			editNodeParams.fullPath,
			editNode.fullPath,
			editNodeOriginal.fullPath,
			getWidgetFieldData.fullPath,
			canEditWorkflow.fullPath,
			getConfigurationFieldResponse.fullPath,
			isConfigurationsOn.fullPath,
			getAccountConnection.fullPath,
		]),
		nodeAction() {
			return this[getAction.fullPath](this.node.action);
		},
		isWriteAction() {
			return this.nodeAction?.type === 'write';
		},
		nodeService() {
			return this[getService.fullPath](this.node.service);
		},
		nodeServiceId() {
			return this.nodeService?.id;
		},
		nodeServiceVersion() {
			return this.node.service_version;
		},
		actionParams() {
			return this.node && this[getActionParams.fullPath](this.nodeAction.id);
		},
		allActionParams() {
			if (!this[editNodeParams.fullPath].length || this.node.id !== this[editNode.fullPath].id) {
				return this.actionParams;
			}
			return this[editNodeParams.fullPath];
		},
		workflowVersion() {
			return this[editedWorkflowVersion.fullPath];
		},
		requiredActionParams() {
			return this.params?.filter((ap) => ap.required);
		},
		editNodeOriginal() {
			return this[editNodeOriginal.fullPath];
		},
		editNodeOriginalParamsMap() {
			// todo: what is this? do we really need it?
			return this.editNodeOriginal.params_map;
		},
		filledMapper() {
			return !!this.node.user_schema_mappings_source_field_uuid && this.isMappingSetupFinished;
		},
		mapperData() {
			if (!this.node.user_schema_mappings_source_field_uuid) {
				return {};
			}
			return (this[getWidgetFieldData.fullPath](this.node.user_schema_mappings_source_field_uuid) || {}).widgetField || {};
		},
		isMappingSetupFinished() {
			return DynamicDataService.isMappingSetupFinished(this.mapperData, this.nodeService);
		},
		hasEditPermissions() {
			return this[canEditWorkflow.fullPath](this.node?.workflow) && !this.isLocked;
		},
		isLocked() {
			return AppService.doesRequiresAuth(this.nodeService) && !this.currentConnection;
		},
		nodeCredentialsId() {
			return WorkflowNodeService.getNodeRelevantCredsId(
				this.workflowVersion,
				this.node,
				this[getConfigurationFieldResponse.fullPath],
				this[isConfigurationsOn.fullPath]
			);
		},
		currentConnection() {
			if (!this.nodeCredentialsId) {
				return null;
			}
			return this[getAccountConnection.fullPath](this.nodeCredentialsId);
		},
	},

	methods: {
		...mapActions('selectedNode', ['setParamInEditNode']),
		buildFieldKey(param) {
			return ParamsService.buildFieldKey(param, this.editNodeOriginalParamsMap);
		},
		updateInputField(payload) {
			const { map, label, param } = payload;
			this.updateBlockParam({ ...map, reloadDynamic: param?.dynamic });

			// TODO - make sure we dont need this anymore,
			// as this is not used and updateInputfieldLabel is being called directly when needed
			if (label) {
				this.updateInputFieldLabel({ label });
			}
		},
		updateInputFieldLabel(payload) {
			const { label } = payload;
			this.updateBlockParamLabels(label);
		},
		updateInputFallback(payload) {
			if (payload) {
				this.updateParamsFallback(payload);
			}
		},
		updateBlockParam(payload) {
			const { key, value, reloadDynamic } = payload;
			this.handleBlockParamDependecies(key);
			const paramsMap = cloneDeep(this.node.params_map) || {};
			paramsMap[key] = value;
			this.setParamInEditNode({ key: 'params_map', value: paramsMap, options: { reloadDynamic } });
		},
		updateBlockParamLabels(payload) {
			const { key, value } = payload;
			const paramsMapLabels = cloneDeep(this.node.params_map_labels) || {};
			paramsMapLabels[key] = value;
			this.setParamInEditNode({ key: 'params_map_labels', value: paramsMapLabels });
		},
		removeParamKey(key) {
			const paramsMap = cloneDeep(this.node.params_map) || {};
			delete paramsMap[key];
			this.setParamInEditNode({ key: 'params_map', value: paramsMap });
		},
		removeParamLabel(key) {
			const paramsMapLabels = cloneDeep(this.node.params_map_labels) || {};
			delete paramsMapLabels[key];
			this.setParamInEditNode({ key: 'params_map_labels', value: paramsMapLabels });
		},
		updateParamsFallback(payload) {
			const { key, value } = payload;
			const fallbacks = cloneDeep(this.node.fallbacks) || {};
			fallbacks[key] = value;
			this.setParamInEditNode({ key: 'fallbacks', value: fallbacks });
		},
		handleBlockParamDependecies(keyChanged) {
			this.actionParams.forEach((param) => {
				const dependsOn = param.depends_on || [];
				if (dependsOn.includes(keyChanged)) {
					this.removeParamKey(param.key);
					this.removeParamLabel(param.key);
				}
			});
		},
	},
};
</script>
